import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ArrowLeft, ArrowRight, Check, XCircle, ShieldCheck, Send, Loader2, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { getLoadStatusClasses, getInvoiceStatusClasses } from '@/lib/statusColors';

const milesToKm = (miles) => Math.round((miles || 0) * 1.60934);

export default function AdminCustomerDetail() {
  const { customerId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const [customer, setCustomer] = useState(null);
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [loading, setLoading] = useState(true);
  const [showVerify, setShowVerify] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [activity, setActivity] = useState([]);
  const [paymentSettings, setPaymentSettings] = useState({
    invoice_frequency: 'per_load',
    payment_terms: 'net_30',
    auto_invoice: false,
  });

  const fetchData = () => {
    if (!token || !customerId) return;
    api.get(`/customers/${customerId}`)
      .then(r => setCustomer(r.data))
      .catch(() => toast.error('Failed to load customer'))
      .finally(() => setLoading(false));
  };

  const fetchNotes = () => {
    if (!token || !customerId) return;
    api.get(`/clients/${customerId}/notes`)
      .then(r => setNotes(r.data))
      .catch(() => setNotes([]));
  };

  const fetchInvoices = () => {
    if (!token || !customerId) return;
    api.get('/invoices')
      .then(r => {
        const clientInvoices = (r.data || []).filter(inv => String(inv.client_id) === String(customerId));
        setInvoices(clientInvoices);
      })
      .catch(() => setInvoices([]));
  };

  const fetchActivity = () => {
    api.get(`/loads?client_id=${customerId}`).then(r => {
      const loads = r.data || [];
      const events = loads.map(l => ({
        type: 'load', date: l.created_at, status: l.status,
        desc: `Load ${l.load_id?.slice(-6)} - ${l.cargo_description || 'Load'}`,
        detail: `${l.pickup_name || ''} → ${l.delivery_name || ''} | $${l.rate || 0}`,
      }));
      events.sort((a, b) => new Date(b.date) - new Date(a.date));
      setActivity(events.slice(0, 50));
    }).catch(() => {});
  };
  useEffect(() => { fetchData(); fetchNotes(); fetchInvoices(); fetchActivity(); }, [token, customerId]);

  const handleVerify = async (status) => {
    if (status === 'verified') {
      const missing = [];
      if (!customer?.company_name) missing.push('Company name');
      if (!customer?.email) missing.push('Email');
      if (!customer?.phone) missing.push('Phone');
      if (missing.length > 0) { toast.error(`Cannot verify: missing ${missing.join(', ')}`); return; }
    }
    try {
      await api.put(`/clients/${customerId}/verify`, { status });
      toast.success(`Client ${status}`);
      setCustomer(prev => ({ ...prev, verification_status: status }));
    } catch { toast.error('Failed'); }
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    try {
      await api.post(`/clients/${customerId}/notes`, { content: newNote.trim() });
      setNewNote(''); fetchNotes(); toast.success('Note added');
    } catch { toast.error('Failed'); }
  };

  const handleSavePaymentSettings = () => {
    toast.success('Settings saved');
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 text-muted-foreground animate-spin" /></div>;
  if (!customer) return <div className="text-center py-12"><p className="text-muted-foreground">Customer not found</p><Button variant="outline" onClick={() => navigate('/admin/customers')} className="mt-4 border-input text-muted-foreground">Back to Customers</Button></div>;

  const avgPerLoad = customer.total_loads > 0 ? Math.round((customer.total_spent || 0) / customer.total_loads) : 0;

  const totalPaid = invoices.filter(i => i.status === 'paid').reduce((sum, i) => sum + (i.amount || 0), 0);
  const totalOutstanding = invoices.filter(i => i.status !== 'paid' && i.status !== 'draft').reduce((sum, i) => sum + (i.amount || 0), 0);

  return (
    <div>
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Button variant="ghost" onClick={() => navigate('/admin/customers')} className="text-muted-foreground hover:text-foreground p-2 h-auto">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex items-center gap-4 flex-1">
          <div className="w-16 h-16 rounded-full bg-purple-500/20 flex items-center justify-center text-2xl font-bold text-purple-400">{customer.name?.charAt(0)}</div>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">{customer.name}</h1>
            <p className="text-sm text-muted-foreground">{customer.company_name || customer.email}{customer.phone ? ` · ${customer.phone}` : ''}</p>
          </div>
          <Badge className={`ml-4 text-xs ${customer.verification_status === 'verified' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-amber-500/20 text-amber-400'}`}>
            {customer.verification_status || 'pending'}
          </Badge>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        {[
          { label: 'Total Loads', value: customer.total_loads || 0, accent: 'text-foreground' },
          { label: 'Active', value: customer.active_loads || 0, accent: 'text-purple-400' },
          { label: 'Total Spent', value: `$${(customer.total_spent || 0).toLocaleString()}`, accent: 'text-brand-accent' },
          { label: 'Avg / Load', value: `$${avgPerLoad.toLocaleString()}`, accent: 'text-brand-accent' },
        ].map((s, i) => (
          <Card key={i} className="bg-card border-border"><CardContent className="p-4">
            <p className={`text-xl font-bold ${s.accent}`}>{s.value}</p>
            <p className="text-[10px] text-muted-foreground mt-1">{s.label}</p>
          </CardContent></Card>
        ))}
      </div>

      {/* 3 Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

        {/* Column 1: Details + Verification + Internal Notes + Onboarding Documents */}
        <div className="space-y-6">
          <Card className="bg-card border-border">
            <CardContent className="p-4 space-y-4">
              <p className="text-[10px] text-muted-foreground tracking-wider font-bold">DETAILS</p>
              <Row label="Email" value={customer.email} />
              {customer.phone && <Row label="Phone" value={customer.phone} />}
              {customer.company_name && <Row label="Company" value={customer.company_name} />}
              <Row label="Total KM" value={`${(customer.total_km || 0).toLocaleString()} km`} />
              <Row label="Completed" value={customer.completed_loads || 0} />

              {customer.company_address && (
                <div className="border-t border-border pt-3">
                  <p className="text-[10px] text-muted-foreground tracking-wider font-bold mb-1">ADDRESS</p>
                  <p className="text-xs text-muted-foreground">{customer.company_address}</p>
                </div>
              )}

              <div className="border-t border-border pt-3">
                <p className="text-[10px] text-muted-foreground tracking-wider font-bold mb-2">VERIFICATION</p>
                <div className="flex items-center justify-between mb-2">
                  <Badge className={customer.verification_status === 'verified' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-amber-500/20 text-amber-400'}>
                    {customer.verification_status || 'pending'}
                  </Badge>
                  {customer.verification_status !== 'verified' && (
                    <Button size="sm" className="bg-emerald-600 text-foreground h-7 text-xs" onClick={() => setShowVerify(true)}>
                      <ShieldCheck className="w-3 h-3 mr-1" /> Verify
                    </Button>
                  )}
                </div>
                {[{ ok: !!customer.company_name, label: 'Company name' }, { ok: !!customer.email, label: 'Email confirmed' }, { ok: !!customer.phone, label: 'Phone number' }].map(ch => (
                  <div key={ch.label} className={`text-[10px] flex items-center gap-1.5 mb-1 ${ch.ok ? 'text-emerald-400' : 'text-muted-foreground'}`}>
                    {ch.ok ? <Check className="w-3 h-3" /> : <XCircle className="w-3 h-3" />} {ch.label}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Activity Log */}
          <Card className="bg-card border-border">
            <CardContent className="p-4 space-y-3">
              <p className="text-[10px] text-muted-foreground tracking-wider font-bold">ACTIVITY LOG</p>
              <div className="space-y-0 max-h-[300px] overflow-auto">
                {activity.length === 0 ? <p className="text-muted-foreground text-xs py-2">No activity yet</p> :
                  activity.map((ev, i) => (
                    <div key={i} className="flex gap-2 pb-3">
                      <div className="flex flex-col items-center">
                        <div className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${
                          ev.status === 'completed' ? 'bg-emerald-400' :
                          ev.status === 'in_transit' ? 'bg-blue-400' :
                          ev.status === 'assigned' ? 'bg-purple-400' : 'bg-muted-foreground'
                        }`} />
                        {i < activity.length - 1 && <div className="w-px flex-1 bg-border mt-1" />}
                      </div>
                      <div>
                        <p className="text-xs text-foreground">{ev.desc}</p>
                        <p className="text-[10px] text-muted-foreground">{ev.detail}</p>
                        <p className="text-[10px] text-muted-foreground">{new Date(ev.date).toLocaleString()}</p>
                      </div>
                    </div>
                  ))
                }
              </div>
            </CardContent>
          </Card>

          {/* Internal Notes */}
          <Card className="bg-card border-border">
            <CardContent className="p-4 space-y-4">
              <p className="text-[10px] text-muted-foreground tracking-wider font-bold">INTERNAL NOTES</p>
              <div className="space-y-2 max-h-[250px] overflow-auto">
                {notes.length === 0 ? <p className="text-muted-foreground text-xs py-2">No notes yet</p> :
                  notes.map(n => (
                    <div key={n.note_id} className="bg-muted/50 rounded-lg p-2.5">
                      <p className="text-xs text-muted-foreground">{n.content}</p>
                      <p className="text-[10px] text-muted-foreground mt-1">{n.author_name} · {new Date(n.created_at).toLocaleDateString()}</p>
                    </div>
                  ))
                }
              </div>
              <div className="flex gap-2">
                <Input value={newNote} onChange={e => setNewNote(e.target.value)} placeholder="Add a note..."
                  className="bg-muted border-input text-foreground text-xs" onKeyDown={e => e.key === 'Enter' && handleAddNote()} />
                <Button size="sm" onClick={handleAddNote} disabled={!newNote.trim()} className="bg-brand-accent h-9"><Send className="w-3.5 h-3.5" /></Button>
              </div>
            </CardContent>
          </Card>

          {/* Onboarding Documents */}
          <Card className="bg-card border-border">
            <CardContent className="p-4 space-y-4">
              <p className="text-[10px] text-muted-foreground tracking-wider font-bold">ONBOARDING DOCUMENTS</p>
              <div className="flex flex-col items-center justify-center py-6 text-center">
                <FileText className="w-8 h-8 text-muted-foreground mb-2" />
                <p className="text-xs text-muted-foreground">No documents uploaded</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Column 2: Payment Activity + Payment Settings */}
        <div className="space-y-6">
          <Card className="bg-card border-border">
            <CardContent className="p-4 space-y-4">
              <p className="text-[10px] text-muted-foreground tracking-wider font-bold">PAYMENT ACTIVITY</p>

              {/* Payment Totals */}
              <div className="grid grid-cols-2 gap-2">
                <div className="bg-muted/50 rounded-lg p-3 text-center">
                  <p className="text-lg font-bold text-emerald-400">${totalPaid.toLocaleString()}</p>
                  <p className="text-[10px] text-muted-foreground">PAID</p>
                </div>
                <div className="bg-muted/50 rounded-lg p-3 text-center">
                  <p className="text-lg font-bold text-amber-400">${totalOutstanding.toLocaleString()}</p>
                  <p className="text-[10px] text-muted-foreground">OUTSTANDING</p>
                </div>
              </div>

              {/* Invoice List */}
              <div className="space-y-2 max-h-[350px] overflow-y-auto">
                {invoices.length === 0 ? (
                  <p className="text-muted-foreground text-xs py-4 text-center">No invoices found</p>
                ) : (
                  invoices.map(inv => (
                    <div key={inv.invoice_id || inv.id} className="bg-muted/50 rounded-lg p-3">
                      <div className="flex items-center justify-between mb-1">
                        <p className="text-xs text-foreground font-medium font-mono">{inv.invoice_number || `INV-${inv.invoice_id || inv.id}`}</p>
                        <Badge className={`text-[10px] ${getInvoiceStatusClasses(inv.status)}`}>
                          {(inv.status || 'draft').toUpperCase()}
                        </Badge>
                      </div>
                      <div className="flex items-center justify-between mt-1">
                        <span className="text-[10px] text-muted-foreground">{inv.created_at ? new Date(inv.created_at).toLocaleDateString() : '-'}</span>
                        <span className="text-xs text-brand-accent font-mono">${(inv.amount || 0).toLocaleString()}</span>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>

          {/* Payment Settings */}
          <Card className="bg-card border-border">
            <CardContent className="p-4 space-y-4">
              <p className="text-[10px] text-muted-foreground tracking-wider font-bold">PAYMENT SETTINGS</p>

              <div className="space-y-3">
                <div>
                  <label className="text-[10px] text-muted-foreground block mb-1">Invoice Frequency</label>
                  <select
                    value={paymentSettings.invoice_frequency}
                    onChange={e => setPaymentSettings(prev => ({ ...prev, invoice_frequency: e.target.value }))}
                    className="w-full bg-muted border border-input text-foreground text-xs rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-brand-accent"
                  >
                    <option value="per_load">Per Load</option>
                    <option value="weekly">Weekly</option>
                    <option value="monthly">Monthly</option>
                  </select>
                </div>

                <div>
                  <label className="text-[10px] text-muted-foreground block mb-1">Payment Terms</label>
                  <select
                    value={paymentSettings.payment_terms}
                    onChange={e => setPaymentSettings(prev => ({ ...prev, payment_terms: e.target.value }))}
                    className="w-full bg-muted border border-input text-foreground text-xs rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-brand-accent"
                  >
                    <option value="net_15">Net 15</option>
                    <option value="net_30">Net 30</option>
                    <option value="net_45">Net 45</option>
                    <option value="net_60">Net 60</option>
                  </select>
                </div>

                <div className="flex items-center justify-between">
                  <label className="text-[10px] text-muted-foreground">Auto-Invoice</label>
                  <button
                    type="button"
                    onClick={() => setPaymentSettings(prev => ({ ...prev, auto_invoice: !prev.auto_invoice }))}
                    className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${paymentSettings.auto_invoice ? 'bg-brand-accent' : 'bg-zinc-700'}`}
                  >
                    <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-background transition-transform ${paymentSettings.auto_invoice ? 'translate-x-4' : 'translate-x-0.5'}`} />
                  </button>
                </div>

                <Button size="sm" className="w-full bg-brand-accent text-brand-accent-foreground font-medium text-xs h-8" onClick={handleSavePaymentSettings}>
                  Save Settings
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Column 3: Order History */}
        <Card className="bg-card border-border">
          <CardContent className="p-4 space-y-3">
            <p className="text-[10px] text-muted-foreground tracking-wider font-bold">ORDER HISTORY ({customer.loads?.length || 0})</p>
            <div className="max-h-[600px] overflow-y-auto space-y-2">
              {(!customer.loads || customer.loads.length === 0) && <p className="text-muted-foreground text-xs py-4 text-center">No orders yet</p>}
              {(customer.loads || []).map(ld => (
                <div key={ld.load_id} className="bg-muted/50 rounded-lg p-3">
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-xs text-foreground font-medium truncate">{ld.cargo_description}</p>
                    <Badge className={`text-[10px] ${getLoadStatusClasses(ld.status)}`}>{ld.status?.replace('_', ' ').toUpperCase()}</Badge>
                  </div>
                  <p className="text-[10px] text-muted-foreground">{(ld.pickup_name || ld.pickup_address)?.split(',')[0]} <ArrowRight className="inline w-3 h-3 mx-0.5" /> {(ld.delivery_name || ld.delivery_address)?.split(',')[0]}</p>
                  <div className="flex items-center justify-between mt-1">
                    <span className="text-[10px] text-muted-foreground">{milesToKm(ld.distance_miles)} km</span>
                    <span className="text-[10px] text-brand-accent font-mono">${ld.rate?.toLocaleString()}</span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Verification Dialog */}
      <Dialog open={showVerify} onOpenChange={setShowVerify}>
        <DialogContent className="bg-surface border-border text-foreground max-w-lg">
          <DialogHeader><DialogTitle>Verify Customer</DialogTitle></DialogHeader>
          {customer && (
            <div className="space-y-4">
              <div className="bg-muted/50 rounded-xl p-4 space-y-2">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-full bg-brand-accent/20 flex items-center justify-center text-lg font-bold text-brand-accent">
                    {customer.name?.charAt(0)?.toUpperCase() || '?'}
                  </div>
                  <div>
                    <p className="text-foreground font-medium">{customer.name}</p>
                    <p className="text-xs text-muted-foreground">{customer.email}</p>
                  </div>
                </div>
                {[
                  { label: 'Company', value: customer.company_name, ok: !!customer.company_name },
                  { label: 'Email', value: customer.email, ok: !!customer.email },
                  { label: 'Phone', value: customer.phone, ok: !!customer.phone },
                  { label: 'Address', value: customer.company_address, ok: !!customer.company_address },
                ].map(item => (
                  <div key={item.label} className="flex items-center justify-between py-1.5 border-b border-input/50 last:border-0">
                    <span className="text-xs text-muted-foreground">{item.label}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-foreground">{item.value || '—'}</span>
                      {item.ok ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <XCircle className="w-3.5 h-3.5 text-red-400" />}
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex gap-3">
                <Button className="flex-1 bg-emerald-600 text-foreground font-bold" onClick={() => { handleVerify('verified'); setShowVerify(false); }}>
                  <ShieldCheck className="w-4 h-4 mr-2" /> Verify Customer
                </Button>
                <Button variant="outline" className="flex-1 border-red-500/30 text-red-400 hover:bg-red-500/10" onClick={() => { handleVerify('rejected'); setShowVerify(false); }}>
                  <XCircle className="w-4 h-4 mr-2" /> Reject
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-[10px] text-muted-foreground">{label}</span>
      <span className="text-xs text-foreground">{value}</span>
    </div>
  );
}
